import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { enterprisesActions } from "modules/enterprises/slices";
import { notificationsActions } from "modules/notifications/slices";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import { isPollStoppedSaga } from "modules/polling/sagas";
import { SagaIterator } from "redux-saga";
import {
  all,
  AllEffect,
  call,
  CallEffect,
  delay,
  ForkEffect,
  put,
  race,
  RaceEffect,
  select,
  take,
  TakeEffect,
  takeLatest,
  takeLeading
} from "redux-saga/effects";
import { getAxiosErrorMessage } from "utils/getAxiosErrorMessage";
import { axiosInstance } from "../../axios";
import { keycloak } from "../../keycloak";
import { authActions } from "./slices";
import { apiSessionSelector, userSelector } from "./selectors";
import {
  ApiLoginResponse,
  ApiSession,
  GetUserDetailsParams,
  KeycloakUser
} from "./types";

const serviceApiPath = `gotham-echo/method/`;

export function* apiReloginSaga() {
  try {
    yield* apiLogoutSaga();
    yield* apiLoginSaga();
    yield put(authActions.apiReloginSuccess());
  } catch (e) {
    yield put(
      authActions.apiReloginFailure({ error: getAxiosErrorMessage(e) })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to refresh user session",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* apiLoginSaga() {
  try {
    yield put(authActions.apiLoginRequest());
    const response: AxiosResponse<ApiLoginResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}api-login`
    );
    yield put(authActions.apiLoginSuccess(response.data));
  } catch (e) {
    yield put(authActions.apiLoginFailure({ error: getAxiosErrorMessage(e) }));
  }
}

export function* apiLogoutSaga() {
  try {
    yield put(authActions.apiLogoutRequest());
    yield call(axiosInstance.get, `${serviceApiPath}api-logout`);
    yield put(authActions.apiLogoutSuccess());
  } catch (e) {
    yield put(authActions.apiLogoutFailure({ error: getAxiosErrorMessage(e) }));
  }
}

export function* logoutSaga(): SagaIterator<void> {
  try {
    yield call(apiLogoutSaga);
    yield call(keycloak.logout);
    yield put(authActions.logoutSuccess());
    yield put(authActions.stopRefreshApiSessionPolling());
  } catch (e) {
    yield put(authActions.logoutFailure({ error: getAxiosErrorMessage(e) }));
    yield put(
      notificationsActions.showNotification({
        title: "Failed to log out",
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

function* refreshApiSessionPollingWorkerSaga() {
  while (true) {
    const apiSession: ApiSession | null = yield select(apiSessionSelector);
    const POLLING_INTERVAL = 30 * 1000; // in milliseconds

    if (apiSession) {
      const tokenRemainingLifeTime = apiSession.exp * 1000 - Date.now();
      if (tokenRemainingLifeTime <= POLLING_INTERVAL) {
        yield put(authActions.apiReloginRequest());
      }
      yield delay(POLLING_INTERVAL);
    }
  }
}

function* refreshApiSessionPollingWatcherSaga() {
  while (true) {
    yield take(authActions.startRefreshApiSessionPolling);
    yield race({
      task: call(refreshApiSessionPollingWorkerSaga),
      cancel: take(authActions.stopRefreshApiSessionPolling)
    });
  }
}

export function* getUserDetailsSaga(
  action: PayloadAction<GetUserDetailsParams>
): SagaIterator<void> {
  try {
    const user: KeycloakUser = yield call(keycloak.loadUserProfile);
    user.id = keycloak.subject;
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    const userDetails = yield select(userSelector);
    if (!isPollStopped) {
      yield put(authActions.getUserDetailsSuccess(user));
    }
    const hubspotCookie = document.cookie
      .split("; ")
      .find((cookie) => cookie.startsWith("hubspotutk"));
    const hutk = hubspotCookie?.slice(hubspotCookie.indexOf("=") + 1);

    if (!userDetails && !user.attributes?.["hs:submission"] && hutk) {
      yield put(
        enterprisesActions.submitHubSpotFormRequest({
          hutk
        })
      );
    }
  } catch (e) {
    yield put(
      authActions.getUserDetailsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get user profile",
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* watcherSaga(): Generator<
  AllEffect<
    | Generator<
        TakeEffect | RaceEffect<TakeEffect | CallEffect<void>>,
        void,
        unknown
      >
    | ForkEffect<never>
  >,
  void,
  unknown
> {
  yield all([
    refreshApiSessionPollingWatcherSaga(),
    takeLatest(authActions.apiReloginRequest, apiReloginSaga),
    takeLeading(authActions.getUserDetailsRequest.type, getUserDetailsSaga),
    takeLatest(authActions.logoutRequest, logoutSaga)
  ]);
}
